import React from "react";
import { useNavigate } from "react-router-dom";
import { useCurrencyFormatter } from "../../../../Components/Hooks/useCurrencyFormatter";

const STATUS_CONFIG = {
  saved:    { label: "Saved",    bg: "rgba(99,179,237,0.2)",  color: "#90cdf4" },
  pending:  { label: "Pending",  bg: "rgba(251,191,36,0.2)",  color: "#fcd34d" },
  canceled: { label: "Canceled", bg: "rgba(248,113,113,0.2)", color: "#fca5a5" },
};

const StatPill = ({ icon, label, subtle }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.08)", borderRadius: 20, padding: "5px 12px", border: "1px solid rgba(255,255,255,0.12)" }}>
    <i className={icon} style={{ fontSize: "0.85rem", color: "#adc7f7" }} />
    <span style={{ fontSize: "0.78rem", fontWeight: 600, color: subtle ? "#adc7f7" : "white" }}>{label}</span>
  </div>
);

const DimensionRow = ({ label, items, color }) => {
  if (!items?.length) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
      <span style={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "rgba(255,255,255,0.35)", minWidth: 84, flexShrink: 0 }}>
        {label}
      </span>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
        {items.map((name) => (
          <span key={name} style={{ fontSize: "0.72rem", fontWeight: 600, background: `${color}18`, color, border: `1px solid ${color}40`, borderRadius: 12, padding: "2px 10px" }}>
            {name}
          </span>
        ))}
      </div>
    </div>
  );
};

const VendorHeader = ({ vendorName, cif, stats }) => {
  const navigate = useNavigate();
  const { formatCurrency } = useCurrencyFormatter();

  const amountEntries = Object.entries(stats?.byAmount || {}).filter(([, v]) => v > 0);
  const statusEntries = Object.entries(stats?.byStatus || {});

  return (
    <div className="mb-4">
      <button
        type="button"
        onClick={() => navigate("/financial/invoices/vendors")}
        style={{ background: "none", border: "none", padding: "0 0 12px 0", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: "#74777f", fontSize: "0.8rem", fontWeight: 600 }}
      >
        <i className="ri-arrow-left-line" />
        Back to Vendors
      </button>

      <div style={{ background: "linear-gradient(135deg, #002045 0%, #1a365d 100%)", borderRadius: 16, padding: "24px 28px", position: "relative", overflow: "hidden" }}>
        <i className="ri-building-2-fill" style={{ position: "absolute", right: -20, bottom: -20, fontSize: 160, color: "white", opacity: 0.04 }} />

        {/* Vendor identity */}
        <div className="d-flex align-items-center gap-3 mb-3">
          <div style={{ width: 48, height: 48, borderRadius: 12, background: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <i className="ri-store-2-line" style={{ fontSize: "1.4rem", color: "white" }} />
          </div>
          <div>
            <h4 className="mb-0 fw-bold text-white" style={{ lineHeight: 1.2 }}>
              {vendorName || cif || "Vendor"}
            </h4>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
              <span style={{ fontSize: "0.72rem", fontWeight: 700, background: "rgba(255,255,255,0.12)", color: "#adc7f7", borderRadius: 6, padding: "2px 8px", letterSpacing: "0.05em" }}>
                CIF {cif}
              </span>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <StatPill icon="ri-file-list-3-line" label={stats ? `${stats.total} invoice${stats.total !== 1 ? "s" : ""}` : "Loading…"} />
          {amountEntries.map(([currency, amount]) => (
            <StatPill key={currency} icon="ri-money-euro-circle-line"
              label={formatCurrency(amount, currency, false, false)} />
          ))}
          {statusEntries.map(([status, count]) => {
            const cfg = STATUS_CONFIG[status] || { label: status, bg: "rgba(255,255,255,0.1)", color: "#e5e7eb" };
            return (
              <div key={status} style={{ display: "flex", alignItems: "center", gap: 5, background: cfg.bg, borderRadius: 20, padding: "5px 12px", border: `1px solid ${cfg.color}33` }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.color, flexShrink: 0 }} />
                <span style={{ fontSize: "0.78rem", fontWeight: 600, color: cfg.color }}>{count} {cfg.label}</span>
              </div>
            );
          })}
        </div>

        {/* Dimensions */}
        {stats && (stats.costTypes?.length > 0 || stats.categories?.length > 0 || stats.subCategories?.length > 0 || stats.blocks?.length > 0) && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
            <DimensionRow label="Cost Type"    items={stats.costTypes}     color="#c4b5fd" />
            <DimensionRow label="Category"     items={stats.categories}    color="#93c5fd" />
            <DimensionRow label="Sub-category" items={stats.subCategories} color="#86efac" />
            <DimensionRow label="Block"        items={stats.blocks}        color="#fcd34d" />
          </div>
        )}

      </div>
    </div>
  );
};

export default VendorHeader;
